import { useEffect, useMemo, useState } from "react";
import {
  Crown,
  Gift,
  Sparkles,
  Wallet,
  History,
  Zap,
  ChevronRight,
  CheckCircle2,
  Star,
  ArrowRightLeft,
  Coins,
  X,
  Info,
  Trophy,
  Gem,
  Loader2,
  Lock,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

function fmt(n, d = 0) {
  return Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: d,
    minimumFractionDigits: 0,
  });
}

const TIER_ICONS = {
  Normal: Crown,
  Silver: Star,
  Gold: Trophy,
  Platinum: Gem,
  Diamond: Sparkles,
};

export default function MyVipPage() {
  const [vip, setVip] = useState(null);
  const [history, setHistory] = useState([]);
  const [claiming, setClaiming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertPoints, setConvertPoints] = useState("");
  const [converting, setConverting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: h }] = await Promise.all([
        api.get("/profile/vip"),
        api.get("/profile/vip/history").catch(() => ({ data: { items: [] } })),
      ]);
      setVip(data);
      setHistory(h?.items || []);
    } catch {
      setVip({
        current_tier: "Normal",
        next_tier: "Silver",
        points: 0,
        cashback_pending: 0,
        lifetime_turnover: 0,
        tiers: [],
        points_conversion_rate: 100,
        points_min_convert: 100,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const claim = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const { data } = await api.post("/profile/vip/claim");
      toast.success(`৳${fmt(data.credited, 2)} আপনার balance-এ যোগ হয়েছে`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    } finally {
      setClaiming(false);
    }
  };

  const convert = async () => {
    const pts = parseInt(convertPoints, 10);
    if (!pts || pts <= 0) {
      toast.error("সঠিক points সংখ্যা লিখুন");
      return;
    }
    setConverting(true);
    try {
      const { data } = await api.post("/profile/vip/convert-points", {
        points: pts,
      });
      toast.success(
        `🎉 ${fmt(data.points_used)} points → ৳${fmt(data.credited, 2)} credited!`
      );
      setConvertOpen(false);
      setConvertPoints("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally {
      setConverting(false);
    }
  };

  const tierName = vip?.current_tier || "Normal";
  const tiers = vip?.tiers || [];
  const currentTier =
    tiers.find((t) => t.name === tierName) ||
    tiers[0] || { color: "#8a97a8", perks: [] };
  const tierColor = vip?.current_color || currentTier?.color || "#8a97a8";
  const perks =
    vip?.perks?.length ? vip.perks : currentTier?.perks || [];
  const pending = Number(vip?.cashback_pending || 0);
  const claimedTotal = Number(vip?.cashback_claimed_total || 0);
  // Cashback gate fields from backend
  const canClaim = vip?.can_claim_cashback !== false; // default true for legacy payloads
  const blockReason = vip?.claim_block_reason || null;
  const blockMessage = vip?.claim_block_message || "";
  const claimMinAmount = Number(vip?.claim_min_amount || 50);
  const cooldownRemain = Number(vip?.claim_cooldown_remaining_seconds || 0);
  const netWin = Number(vip?.net_win || 0);
  // 7-day weekly cycle info
  const weeklyCycle = vip?.weekly_cycle || null;
  const cycleDaysRemain = weeklyCycle ? Number(weeklyCycle.days_remaining || 0) : null;

  // Bengali reason map (preferred over backend English message for our players)
  const banglaReason = (() => {
    if (!blockReason) return null;
    switch (blockReason) {
      case "blocked":
        return "আপনার অ্যাকাউন্ট সাময়িকভাবে স্থগিত — Support-এ যোগাযোগ করুন।";
      case "no_pending":
        return "এখনো কোনো cashback জমা হয়নি।";
      case "in_profit":
        return `আপনি বর্তমানে net profit-এ আছেন (৳${fmt(netWin, 2)})। লাভের সময় cashback claim করা যাবে না — net loss-এ আসার পর আবার unlock হবে।`;
      case "below_min":
        return `সর্বনিম্ন claim amount ৳${fmt(claimMinAmount, 2)} — আরো একটু খেললেই cashback আনলক হবে।`;
      case "cooldown": {
        const h = Math.floor(cooldownRemain / 3600);
        const m = Math.floor((cooldownRemain % 3600) / 60);
        return `পরবর্তী claim করতে ${h}ঘ ${m}মি বাকি আছে।`;
      }
      default:
        return blockMessage;
    }
  })();
  const lifetimeTo = Number(vip?.lifetime_turnover || 0);
  const toNext = Number(vip?.turnover_to_next || 0);
  const progressPct = Math.min(
    100,
    Math.max(0, (vip?.progress || 0) * 100)
  );
  const points = Number(vip?.points || 0);
  const rate = Number(vip?.points_conversion_rate || 100);
  const minConvert = Number(vip?.points_min_convert || 100);
  const convertibleBdt = useMemo(
    () => Math.floor(points / rate),
    [points, rate]
  );

  const TierIcon = TIER_ICONS[tierName] || Crown;

  return (
    <ProfileShell title="My VIP">
      <div className="py-2 pb-10 space-y-4" data-testid="vip-page">
        {/* ============ TIER HERO CARD ============ */}
        <div
          className="rounded-3xl relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${tierColor}30 0%, #0a1119 55%, #0a1119 100%)`,
            border: `1px solid ${tierColor}55`,
            boxShadow: `0 24px 60px ${tierColor}25, 0 0 0 1px ${tierColor}15`,
          }}
        >
          {/* glow orbs */}
          <div
            aria-hidden
            className="absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl opacity-50 pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${tierColor} 0%, transparent 60%)`,
            }}
          />
          <div
            aria-hidden
            className="absolute -left-12 -bottom-12 h-44 w-44 rounded-full blur-3xl opacity-25 pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${tierColor} 0%, transparent 60%)`,
            }}
          />
          {/* grid pattern */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />

          <div className="relative p-6">
            {/* badge */}
            <div className="flex items-center gap-2 mb-4">
              <span
                className="inline-flex items-center gap-1 px-2.5 h-6 rounded-full text-[10px] font-extrabold uppercase tracking-[0.2em]"
                style={{
                  background: `${tierColor}25`,
                  color: tierColor,
                  border: `1px solid ${tierColor}45`,
                }}
              >
                <Sparkles size={10} /> VIP MEMBER
              </span>
              <span className="text-white/30 text-[10px] uppercase tracking-wider font-bold ml-auto">
                {tierName} TIER
              </span>
            </div>

            <div className="flex items-center gap-4">
              {/* Animated tier icon */}
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute inset-[-3px] rounded-2xl"
                  style={{
                    background: `conic-gradient(from 0deg, ${tierColor}, ${tierColor}80, white, ${tierColor})`,
                    animation: "spinIconRotate 8s linear infinite",
                  }}
                />
                <div
                  className="relative h-16 w-16 rounded-2xl flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${tierColor} 0%, #0d1620 100%)`,
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.25)`,
                  }}
                >
                  <TierIcon
                    size={28}
                    className="text-white"
                    strokeWidth={1.8}
                    fill={tierName !== "Normal" ? "rgba(255,255,255,0.15)" : "none"}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div
                  data-testid="vip-current-tier"
                  className="text-3xl sm:text-[34px] font-black leading-none tracking-tight"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${tierColor} 0%, #ffffff 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {tierName}
                </div>
                <div className="mt-1.5 text-[11px] text-white/55 inline-flex items-center gap-1.5">
                  <Zap size={11} className="text-amber-400" />
                  <span data-testid="vip-rates">
                    {Number(vip?.rakeback_pct || 0).toFixed(2)}% rakeback ·{" "}
                    {Number(vip?.cashback_pct || 0).toFixed(2)}% cashback
                  </span>
                </div>
              </div>
            </div>

            {/* Lifetime turnover progress */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-[11px] mb-2">
                <span className="text-white/50 inline-flex items-center gap-1.5">
                  <TrendingUp size={11} /> ৳{fmt(lifetimeTo)} lifetime
                </span>
                {vip?.next_tier ? (
                  <span className="text-white/55">
                    ৳{fmt(toNext)} to{" "}
                    <span
                      style={{ color: vip?.next_color || "#fff" }}
                      className="font-bold"
                    >
                      {vip.next_tier}
                    </span>
                  </span>
                ) : (
                  <span className="text-emerald-300 inline-flex items-center gap-1 font-bold">
                    <Crown size={11} /> Top tier!
                  </span>
                )}
              </div>
              <div className="relative h-2.5 rounded-full bg-white/5 overflow-hidden border border-white/5">
                <div
                  data-testid="vip-progress"
                  className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700"
                  style={{
                    width: `${Math.max(2, progressPct)}%`,
                    background: `linear-gradient(90deg, ${tierColor} 0%, white 100%)`,
                    boxShadow: `0 0 16px ${tierColor}99`,
                  }}
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)",
                    animation: "shineVIP 2.4s ease-in-out infinite",
                  }}
                />
              </div>
              <div className="text-right text-[10px] text-white/40 font-bold mt-1.5 tabular-nums">
                {progressPct.toFixed(1)}% to next tier
              </div>
            </div>
          </div>
        </div>

        {/* ============ VIP POINTS WALLET (NEW) ============ */}
        <div
          className="rounded-3xl relative overflow-hidden p-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(245,196,81,0.18) 0%, rgba(10,17,25,0.95) 70%)",
            border: "1px solid rgba(245,196,81,0.35)",
            boxShadow: "0 16px 40px rgba(245,196,81,0.15)",
          }}
          data-testid="vip-points-card"
        >
          <div
            aria-hidden
            className="absolute -right-12 -top-12 h-44 w-44 rounded-full blur-3xl opacity-50 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(245,196,81,0.55), transparent 60%)",
            }}
          />

          <div className="flex items-start gap-3 relative">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background:
                  "linear-gradient(135deg, #f5c451 0%, #b8842a 100%)",
                boxShadow:
                  "0 12px 28px rgba(245,196,81,0.45), inset 0 1px 0 rgba(255,255,255,0.3)",
              }}
            >
              <Coins size={26} className="text-[#1a1408]" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-amber-300 text-[10px] uppercase tracking-[0.2em] font-extrabold">
                VIP POINTS
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span
                  className="text-[40px] sm:text-[48px] font-black tabular-nums leading-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(180deg, #ffe27a 0%, #f5c451 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                  data-testid="vip-points"
                >
                  {fmt(points)}
                </span>
                <span className="text-amber-300/60 text-sm font-bold">pts</span>
              </div>
              <div className="text-white/55 text-[12px] mt-1">
                ≈ <span className="text-emerald-300 font-bold">৳{fmt(convertibleBdt)}</span>{" "}
                <span className="text-white/35">
                  ({fmt(rate)} pts = ৳1)
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setConvertOpen(true)}
            disabled={points < minConvert}
            className="w-full mt-5 h-12 rounded-xl text-[14px] font-extrabold inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed relative overflow-hidden group"
            style={{
              background:
                points >= minConvert
                  ? "linear-gradient(135deg, #f5c451 0%, #d97706 100%)"
                  : "rgba(255,255,255,0.05)",
              color: points >= minConvert ? "#1a1408" : "rgba(255,255,255,0.45)",
              boxShadow:
                points >= minConvert
                  ? "0 12px 28px rgba(245,196,81,0.45), inset 0 1px 0 rgba(255,255,255,0.3)"
                  : "none",
            }}
            data-testid="vip-convert-btn"
          >
            {points >= minConvert ? (
              <>
                <span
                  aria-hidden
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)",
                    animation: "btnShineVIP 1.5s linear infinite",
                  }}
                />
                <ArrowRightLeft size={16} />
                Points → Balance কনভার্ট করুন
              </>
            ) : (
              <>
                <Lock size={14} />
                সর্বনিম্ন {minConvert} points লাগবে
              </>
            )}
          </button>

          <div className="mt-3 flex items-start gap-2 text-[11px] text-white/45 leading-relaxed">
            <Info size={11} className="text-amber-400/70 shrink-0 mt-0.5" />
            <span>
              প্রতিটি deposit-এ ১ point ও bet settle হলে অতিরিক্ত points পাবেন।
              Points সরাসরি cash balance-এ রূপান্তরযোগ্য।
            </span>
          </div>
        </div>

        {/* ============ CASHBACK WALLET ============ */}
        <div
          className="rounded-3xl relative overflow-hidden p-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(10,17,25,0.95) 70%)",
            border: "1px solid rgba(25,165,92,0.32)",
            boxShadow: "0 16px 40px rgba(25,165,92,0.15)",
          }}
        >
          <div
            aria-hidden
            className="absolute -right-12 -top-12 h-44 w-44 rounded-full blur-3xl opacity-50 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(33,196,110,0.45), transparent 60%)",
            }}
          />

          <div className="flex items-start gap-3 relative">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background:
                  "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)",
                boxShadow:
                  "0 12px 28px rgba(25,165,92,0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              <Wallet size={26} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-emerald-300 text-[10px] uppercase tracking-[0.2em] font-extrabold">
                CASHBACK WALLET
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-emerald-400/90 text-xl font-bold">৳</span>
                <span
                  className="text-[36px] sm:text-[42px] font-black tabular-nums leading-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(180deg, #ffffff 0%, #bfe8d0 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                  data-testid="vip-pending-amount"
                >
                  {fmt(pending, 2)}
                </span>
                <span className="text-white/40 text-xs">pending</span>
              </div>
              <div className="text-white/45 text-[11px] mt-1">
                Lifetime claimed:{" "}
                <span className="text-emerald-300 font-bold">
                  ৳{fmt(claimedTotal, 2)}
                </span>
              </div>
            </div>
          </div>

          {/* 7-day weekly cycle progress */}
          {weeklyCycle && cycleDaysRemain !== null && (
            <div
              data-testid="vip-weekly-cycle"
              className="mt-4 rounded-xl px-3 py-2.5 flex items-center gap-2.5 text-[12px]"
              style={{
                background: "rgba(14,165,233,0.08)",
                border: "1px solid rgba(14,165,233,0.25)",
                fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif',
              }}
            >
              <History size={14} className="text-sky-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white/80">
                  পরবর্তী cashback হিসাব{" "}
                  <span className="text-sky-300 font-extrabold">
                    {cycleDaysRemain >= 1
                      ? `${Math.floor(cycleDaysRemain)} দিন ${Math.floor((cycleDaysRemain % 1) * 24)} ঘণ্টা`
                      : `${Math.floor(cycleDaysRemain * 24)} ঘণ্টা`}
                  </span>{" "}
                  পর
                </div>
                <div className="text-white/50 text-[10.5px] leading-tight mt-0.5">
                  ৭ দিনে যদি net loss থাকে — তবেই cashback পাবেন। লাভে থাকলে এই cycle skip হবে।
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={claim}
            disabled={claiming || !canClaim}
            className="w-full mt-5 h-12 rounded-xl text-[14px] font-extrabold inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed text-white"
            style={{
              background:
                canClaim
                  ? "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)"
                  : "rgba(255,255,255,0.05)",
              boxShadow:
                canClaim
                  ? "0 12px 28px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.2)"
                  : "none",
            }}
            data-testid="vip-claim-btn"
          >
            {claiming ? (
              <Loader2 size={16} className="animate-spin" />
            ) : canClaim ? (
              <CheckCircle2 size={16} />
            ) : (
              <Lock size={14} />
            )}
            {claiming
              ? "Claiming…"
              : canClaim
              ? "Claim ৳" + fmt(pending, 2)
              : blockReason === "no_pending"
              ? "কোনো cashback নেই"
              : blockReason === "in_profit"
              ? "লাভের সময় claim করা যাবে না"
              : blockReason === "below_min"
              ? `সর্বনিম্ন ৳${fmt(claimMinAmount, 2)}`
              : blockReason === "cooldown"
              ? "Cooldown চলছে"
              : blockReason === "blocked"
              ? "অ্যাকাউন্ট স্থগিত"
              : "Claim unavailable"}
          </button>

          {/* Bengali block-reason explainer */}
          {!canClaim && banglaReason && blockReason !== "no_pending" && (
            <div
              data-testid="vip-claim-block-reason"
              className="mt-3 rounded-lg border px-3 py-2.5 flex items-start gap-2 text-[12px] leading-relaxed"
              style={{
                background: blockReason === "in_profit" ? "rgba(245,158,11,0.08)" : "rgba(14,165,233,0.06)",
                borderColor: blockReason === "in_profit" ? "rgba(245,158,11,0.30)" : "rgba(14,165,233,0.25)",
                fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif',
              }}
            >
              {blockReason === "in_profit" ? (
                <TrendingUp size={14} className="text-amber-400 mt-0.5 shrink-0" />
              ) : (
                <Info size={14} className="text-sky-400 mt-0.5 shrink-0" />
              )}
              <span className="text-white/80">{banglaReason}</span>
            </div>
          )}

          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2 flex items-center gap-1.5">
              <Zap size={11} className="text-amber-400" />
              <span className="text-white/65">Instant rakeback</span>
            </div>
            <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2 flex items-center gap-1.5">
              <Gift size={11} className="text-emerald-400" />
              <span className="text-white/65">Weekly cashback</span>
            </div>
          </div>
        </div>

        {/* ============ TIER PERKS ============ */}
        <div className="rounded-2xl border border-white/5 p-4 bg-white/[0.02]">
          <div className="flex items-center gap-2 text-white/85 mb-3">
            <Sparkles size={15} className="text-amber-400" />
            <span className="text-sm font-bold">{tierName} tier perks</span>
          </div>
          {perks.length === 0 ? (
            <div className="text-white/40 text-sm">
              আপনার tier-এ আরও perks পেতে turnover বাড়ান।
            </div>
          ) : (
            <ul className="grid grid-cols-1 gap-2">
              {perks.map((p, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2.5 text-white/85 text-sm py-1"
                >
                  <span
                    aria-hidden
                    className="h-6 w-6 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: `${tierColor}25`,
                      color: tierColor,
                    }}
                  >
                    <CheckCircle2 size={12} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ============ ALL TIERS ============ */}
        {tiers.length > 0 && (
          <div className="rounded-2xl border border-white/5 overflow-hidden bg-white/[0.02]">
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 text-white/85">
              <Crown size={14} className="text-amber-400" />
              <span className="text-xs font-bold uppercase tracking-wider">
                All VIP Tiers
              </span>
            </div>
            {tiers.map((t, i) => {
              const Icon = TIER_ICONS[t.name] || Crown;
              const isCurrent = t.name === tierName;
              return (
                <div
                  key={t.id || t.name}
                  data-testid={`vip-tier-row-${t.name}`}
                  className={`flex items-center gap-3 px-4 py-3 ${
                    i !== tiers.length - 1 ? "border-b border-white/5" : ""
                  } ${isCurrent ? "bg-white/[0.04]" : ""}`}
                >
                  <span
                    aria-hidden
                    className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${t.color} 0%, #1a2030 100%)`,
                      boxShadow: isCurrent ? `0 0 14px ${t.color}77` : "none",
                    }}
                  >
                    <Icon size={15} className="text-white" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-bold flex items-center gap-1.5"
                      style={{
                        color: isCurrent ? t.color : "rgba(255,255,255,0.85)",
                      }}
                    >
                      {t.name}
                      {isCurrent && (
                        <span
                          className="inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 h-4 rounded-sm font-extrabold"
                          style={{
                            background: "rgba(25,165,92,0.2)",
                            color: "#34d399",
                          }}
                        >
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-white/40 text-[11px] mt-0.5">
                      ৳{fmt(t.min_turnover)} ·{" "}
                      {Number(t.rakeback_pct || 0).toFixed(2)}% rakeback ·{" "}
                      {Number(t.cashback_pct || 0).toFixed(2)}% cashback
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-white/20 shrink-0" />
                </div>
              );
            })}
          </div>
        )}

        {/* ============ HISTORY ============ */}
        <div className="rounded-2xl border border-white/5 overflow-hidden bg-white/[0.02]">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 text-white/85">
            <History size={14} className="text-emerald-400" />
            <span className="text-xs font-bold uppercase tracking-wider">
              Activity History
            </span>
          </div>
          {loading ? (
            <div className="px-4 py-6 text-white/40 text-sm text-center inline-flex items-center justify-center gap-2 w-full">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : history.length === 0 ? (
            <div className="px-4 py-10 text-center" data-testid="vip-history-empty">
              <Sparkles size={32} className="mx-auto text-white/15" />
              <p className="mt-3 text-white/55 text-sm font-semibold">
                এখনো কোনো VIP activity নেই
              </p>
              <p className="mt-1 text-white/35 text-[11px]">
                Bet রাখলে এখানে rakeback ও cashback log দেখা যাবে।
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5" data-testid="vip-history-list">
              {history.slice(0, 20).map((h) => {
                const isClaim = h.type === "claim";
                const isManual = h.type === "manual_grant";
                const isConvert = h.type === "points_convert";
                const tone = isClaim
                  ? "text-emerald-300"
                  : isConvert
                  ? "text-amber-300"
                  : isManual
                  ? "text-amber-300"
                  : "text-sky-300";
                const Icon = isClaim
                  ? Wallet
                  : isConvert
                  ? ArrowRightLeft
                  : isManual
                  ? Gift
                  : Zap;
                return (
                  <div
                    key={h.id}
                    className="px-4 py-3 flex items-center gap-3 text-sm"
                  >
                    <span
                      aria-hidden
                      className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${tone.replace("text-", "bg-").replace("-300", "-500/15")}`}
                    >
                      <Icon size={13} className={tone} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-white/90 text-[13px] truncate font-semibold">
                        {isClaim
                          ? "Cashback claimed"
                          : isConvert
                          ? `${fmt(h.points_used || 0)} points → balance`
                          : isManual
                          ? "Manual grant"
                          : `Auto-accrual · ${h.tier || "Normal"}`}
                      </div>
                      <div className="text-white/35 text-[11px] mt-0.5">
                        {h.created_at
                          ? new Date(h.created_at).toLocaleString()
                          : ""}
                        {h.bet_amount
                          ? ` · stake ৳${fmt(h.bet_amount)}`
                          : ""}
                      </div>
                    </div>
                    <div
                      className={`tabular-nums font-extrabold ${tone}`}
                    >
                      {isClaim || isConvert ? "+" : "+"}৳{fmt(h.total_credited, 2)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ============ CONVERT MODAL ============ */}
      {convertOpen && (
        <ConvertModal
          points={points}
          rate={rate}
          minConvert={minConvert}
          value={convertPoints}
          setValue={setConvertPoints}
          onClose={() => {
            setConvertOpen(false);
            setConvertPoints("");
          }}
          onConfirm={convert}
          loading={converting}
        />
      )}

      <style>{`
        @keyframes spinIconRotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shineVIP { 0%,100% { transform: translateX(-100%); } 50% { transform: translateX(100%); } }
        @keyframes btnShineVIP { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
      `}</style>
    </ProfileShell>
  );
}

/* ---------- Convert Modal ---------- */
function ConvertModal({
  points,
  rate,
  minConvert,
  value,
  setValue,
  onClose,
  onConfirm,
  loading,
}) {
  const numeric = Math.max(0, parseInt(value || "0", 10));
  const credit = (numeric / rate).toFixed(2);
  const valid = numeric >= minConvert && numeric <= points && numeric % minConvert === 0;
  const errorMsg =
    numeric > points
      ? `আপনার কাছে মাত্র ${points} points আছে`
      : numeric > 0 && numeric < minConvert
      ? `সর্বনিম্ন ${minConvert} points`
      : numeric > 0 && numeric % minConvert !== 0
      ? `${minConvert}-এর গুণিতক হতে হবে`
      : "";

  const presets = useMemo(() => {
    const arr = [minConvert, minConvert * 5, minConvert * 10];
    arr.push(Math.floor(points / minConvert) * minConvert); // max
    return [...new Set(arr.filter((p) => p > 0 && p <= points))];
  }, [points, minConvert]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      data-testid="convert-modal"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        style={{ animation: "fadeIn 220ms ease both" }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:w-[420px] rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, #0d1622 0%, #0a1119 100%)",
          border: "1px solid rgba(245,196,81,0.25)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
          animation: "modalSlide 360ms cubic-bezier(0.2,0.9,0.3,1) both",
        }}
      >
        {/* Header */}
        <div
          className="px-5 py-4 border-b border-white/5 flex items-center gap-3 relative"
          style={{
            background:
              "radial-gradient(circle at 100% 0%, rgba(245,196,81,0.25) 0%, transparent 60%)",
          }}
        >
          <div
            className="h-11 w-11 rounded-2xl flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, #f5c451 0%, #b8842a 100%)",
              boxShadow: "0 8px 22px rgba(245,196,81,0.45)",
            }}
          >
            <ArrowRightLeft size={20} className="text-[#1a1408]" strokeWidth={2.4} />
          </div>
          <div className="flex-1">
            <div className="text-white font-extrabold text-[17px]">
              Points → Balance
            </div>
            <div className="text-amber-300/80 text-[11px] uppercase tracking-wider font-bold">
              {fmt(rate)} pts = ৳1
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white"
          >
            <X size={17} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* current balance */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/5">
            <div className="flex items-center gap-2 text-white/55 text-[11px] uppercase tracking-wider font-bold">
              <Coins size={12} className="text-amber-400" /> আপনার Points
            </div>
            <div className="text-amber-300 font-black text-lg tabular-nums">
              {fmt(points)}
            </div>
          </div>

          {/* input */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5 block">
              কত points কনভার্ট করবেন?
            </label>
            <div
              className="relative rounded-xl"
              style={{
                background: "rgba(245,196,81,0.05)",
                border: errorMsg
                  ? "1px solid rgba(244,63,94,0.4)"
                  : valid
                  ? "1px solid rgba(245,196,81,0.4)"
                  : "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <input
                type="number"
                inputMode="numeric"
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder={`${minConvert}`}
                className="w-full h-14 bg-transparent text-white font-extrabold text-2xl px-4 pr-20 outline-none tabular-nums"
                data-testid="convert-input"
                autoFocus
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-300/70 text-xs font-bold uppercase">
                points
              </div>
            </div>
            {errorMsg && (
              <div className="text-rose-300 text-xs mt-1.5 font-semibold">
                ⚠ {errorMsg}
              </div>
            )}
          </div>

          {/* presets */}
          {presets.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-2">
                Quick pick
              </div>
              <div className="grid grid-cols-4 gap-2">
                {presets.map((p, i) => {
                  const label = i === presets.length - 1 && p === Math.floor(points / minConvert) * minConvert ? "MAX" : fmt(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setValue(String(p))}
                      className="h-9 rounded-lg text-xs font-bold transition-colors text-white/80 hover:text-white"
                      style={{
                        background: "rgba(245,196,81,0.08)",
                        border: "1px solid rgba(245,196,81,0.2)",
                      }}
                      data-testid={`convert-preset-${p}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* you receive */}
          <div
            className="rounded-2xl p-4 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(25,165,92,0.18) 0%, rgba(25,165,92,0.02) 100%)",
              border: "1px solid rgba(25,165,92,0.3)",
            }}
          >
            <div className="text-emerald-300 text-[10px] uppercase tracking-[0.2em] font-extrabold mb-1">
              আপনি পাবেন
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-emerald-400 text-2xl font-bold">৳</span>
              <span
                className="text-[40px] font-black tabular-nums leading-none"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #ffffff 0%, #bfe8d0 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
                data-testid="convert-credit-amount"
              >
                {credit}
              </span>
              <span className="text-white/40 text-xs ml-1">main balance</span>
            </div>
            <div className="text-white/40 text-[11px] mt-1">
              {numeric > 0 && `${fmt(numeric)} pts × (1/${fmt(rate)})`}
            </div>
          </div>

          {/* Confirm */}
          <button
            type="button"
            onClick={onConfirm}
            disabled={!valid || loading}
            className="w-full h-12 rounded-xl text-[14px] font-extrabold inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-40"
            style={{
              background: valid
                ? "linear-gradient(135deg, #f5c451 0%, #d97706 100%)"
                : "rgba(255,255,255,0.05)",
              color: valid ? "#1a1408" : "rgba(255,255,255,0.45)",
              boxShadow: valid
                ? "0 12px 28px rgba(245,196,81,0.45), inset 0 1px 0 rgba(255,255,255,0.3)"
                : "none",
            }}
            data-testid="convert-confirm"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Converting...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} /> কনভার্ট নিশ্চিত করুন
              </>
            )}
          </button>

          <div className="text-[11px] text-white/40 text-center">
            <Info size={10} className="inline mb-0.5 mr-0.5" />
            Conversion instant ও irreversible — points-এ ফেরত যাবে না
          </div>
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes modalSlide {
            from { opacity: 0; transform: translateY(40px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}
